import {
  IUserLoginRequestBody,
  IEchoTextAuthClientV1,
  IUserLoginResponseBody,
  IRequestOtpRequestBody,
  IRequestOtpResponseBody,
  IVerifyOtpRequestBody,
  IVerifyOtpResponseBody,
  IVerifyUserRequestBody,
  IVerifyUserResponseBody,
  IChangePasswordRequestBody,
  IChangePasswordResponseBody,
  IForgetPasswordRequestBody,
  IForgetPasswordResponseBody,
  IGetEmployeeRequestBody,
  IGetEmployeeResponseBody,
  IRegistrationRequestBody,
  IRegistrationResponseBody,
  IGetUserResponseBody,
  EventTypes,
  IReducerPayload,
  ILogoutRequestBody,
  ILogoutResponseBody,
  ILoginReportRequestBody,
  ILoginReportResponseBody,
  IChangePasswordReportRequestBody,
  IChangePasswordReportResponseBody,
  IForgotPasswordReportRequestBody,
  IForgotPasswordReportResponseBody,
  IWrongPasswordReportResponseBody,
  IWrongPasswordReportRequestBody,
  IAuthReducer,
} from '@echotex/api-client-auth';
import { IResponse } from '@core-web/networking';
import { useApi } from './useApi';
import { IApiData } from '../types/IApiData';
import { ApiClient, CLIENT_TYPES } from '../clients';
import { useDispatch, useSelector } from 'react-redux';

/**
 * The method to trigger any redux events
 */
export const useRedux = async (type: EventTypes, payload: IReducerPayload): Promise<any> => {
  const dispatch = useDispatch();
  const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
  return dispatch(client.triggerAction(type, payload));
};

/**
 * The method to trigger any redux events
 */
export const useAuthSelector = (): IAuthReducer => {
  const auth = useSelector((state: any) => state.auth || {});
  return auth;
};

/**
 * The user login authentication hook
 */
export const useLoginUser = (): IApiData<
  IResponse<IUserLoginResponseBody>,
  IUserLoginRequestBody,
  IUserLoginResponseBody
> => {
  const dispatch = useDispatch();

  return useApi<IResponse<IUserLoginResponseBody>, IUserLoginRequestBody, IUserLoginResponseBody>(async (query) => {
    const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
    return await client.userLogin(query, dispatch);
  });
};

/**
 * The user logout hook
 */
export const useLogoutUser = (): IApiData<IResponse<ILogoutResponseBody>, ILogoutRequestBody, ILogoutResponseBody> => {
  const dispatch = useDispatch();

  return useApi<IResponse<ILogoutResponseBody>, ILogoutRequestBody, ILogoutResponseBody>(async (query) => {
    const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
    return await client.userLogout(query, dispatch);
  });
};

/**
 * The request otp code hook
 */
export const useRequestOtp = (): IApiData<
  IResponse<IRequestOtpResponseBody>,
  IRequestOtpRequestBody,
  IRequestOtpResponseBody
> => {
  const dispatch = useDispatch();

  return useApi<IResponse<IRequestOtpResponseBody>, IRequestOtpRequestBody, IRequestOtpResponseBody>(async (query) => {
    const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
    return await client.requestOtp(query, dispatch);
  });
};

/**
 * The verify otp code hook
 */
export const useVerifyOtp = (): IApiData<
  IResponse<IVerifyOtpResponseBody>,
  IVerifyOtpRequestBody,
  IVerifyOtpResponseBody
> => {
  const dispatch = useDispatch();

  return useApi<IResponse<IVerifyOtpResponseBody>, IVerifyOtpRequestBody, IVerifyOtpResponseBody>(async (query) => {
    const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
    return await client.verifyOtp(query, dispatch);
  });
};

/**
 * The get user detail method
 */
export const useGetUser = (): IApiData<IResponse<IGetUserResponseBody>, null, IGetUserResponseBody> => {
  const dispatch = useDispatch();

  return useApi<IResponse<IGetUserResponseBody>, null, IGetUserResponseBody>(async () => {
    const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
    return await client.getUser(dispatch);
  });
};

/**
 * The verify user code hook
 */
export const useVerifyUser = (): IApiData<
  IResponse<IVerifyUserResponseBody>,
  IVerifyUserRequestBody,
  IVerifyUserResponseBody
> => {
  return useApi<IResponse<IVerifyUserResponseBody>, IVerifyUserRequestBody, IVerifyUserResponseBody>(async (query) => {
    const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
    return await client.verifyUser(query);
  });
};

/**
 * The hook to update forgotten password
 */
export const useUpdateForgetPassword = (): IApiData<
  IResponse<IForgetPasswordResponseBody>,
  IForgetPasswordRequestBody,
  IForgetPasswordResponseBody
> => {
  return useApi<IResponse<IForgetPasswordResponseBody>, IForgetPasswordRequestBody, IForgetPasswordResponseBody>(
    async (query) => {
      const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
      return await client.updateForgetPassword(query);
    }
  );
};

/**
 * The hook to change password
 */
export const useChangePassword = (): IApiData<
  IResponse<IChangePasswordResponseBody>,
  IChangePasswordRequestBody,
  IChangePasswordResponseBody
> => {
  return useApi<IResponse<IChangePasswordResponseBody>, IChangePasswordRequestBody, IChangePasswordResponseBody>(
    async (query) => {
      const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
      return await client.changePassword(query);
    }
  );
};

/**
 * The hook to get employee information
 */
export const useGetEmployee = (): IApiData<
  IResponse<IGetEmployeeResponseBody>,
  IGetEmployeeRequestBody,
  IGetEmployeeResponseBody
> => {
  return useApi<IResponse<IGetEmployeeResponseBody>, IGetEmployeeRequestBody, IGetEmployeeResponseBody>(
    async (query) => {
      const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
      return await client.getEmployee(query);
    }
  );
};

/**
 * The hook to send registration request for the employee
 */
export const useSendRegistrationRequest = (): IApiData<
  IResponse<IRegistrationResponseBody>,
  IRegistrationRequestBody,
  IRegistrationResponseBody
> => {
  return useApi<IResponse<IRegistrationResponseBody>, IRegistrationRequestBody, IRegistrationResponseBody>(
    async (query) => {
      const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
      return await client.sendRegistrationRequest(query);
    }
  );
};

/**
 * The hook to get login report data from the backend
 */
export const useGetLoginReport = (): IApiData<
  IResponse<ILoginReportResponseBody>,
  ILoginReportRequestBody,
  ILoginReportResponseBody
> => {
  return useApi<IResponse<ILoginReportResponseBody>, ILoginReportRequestBody, ILoginReportResponseBody>(
    async (query) => {
      const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
      return await client.getLoginReport(query);
    }
  );
};

/**
 * The hook to get wrong password attempts report data from the backend
 */
export const useGetWrongPasswordReport = (): IApiData<
  IResponse<IWrongPasswordReportResponseBody>,
  IWrongPasswordReportRequestBody,
  IWrongPasswordReportResponseBody
> => {
  return useApi<
    IResponse<IWrongPasswordReportResponseBody>,
    IWrongPasswordReportRequestBody,
    IWrongPasswordReportResponseBody
  >(async (query) => {
    const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
    return await client.getWrongPasswordReport(query);
  });
};

/**
 * The hook to get forgot password attempts report data from the backend
 */
export const useGetForgotPasswordReport = (): IApiData<
  IResponse<IForgotPasswordReportResponseBody>,
  IForgotPasswordReportRequestBody,
  IForgotPasswordReportResponseBody
> => {
  return useApi<
    IResponse<IForgotPasswordReportResponseBody>,
    IForgotPasswordReportRequestBody,
    IForgotPasswordReportResponseBody
  >(async (query) => {
    const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
    return await client.getForgotPasswordReport(query);
  });
};

/**
 * The hook to get change password attempts report data from the backend
 */
export const useGetChangePasswordReport = (): IApiData<
  IResponse<IChangePasswordReportResponseBody>,
  IChangePasswordReportRequestBody,
  IChangePasswordReportResponseBody
> => {
  return useApi<
    IResponse<IChangePasswordReportResponseBody>,
    IChangePasswordReportRequestBody,
    IChangePasswordReportResponseBody
  >(async (query) => {
    const client = await ApiClient.getClient<IEchoTextAuthClientV1>(CLIENT_TYPES.AUTH_CLIENT);
    return await client.getChangePasswordReport(query);
  });
};
